import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Progress,
  Badge,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import { parseSrt } from '../utils/srtParser';
import { toast } from 'react-toastify';
import HlsVideoPlayer from './HlsVideoPlayer';
import PortraitMediaFrame from './PortraitMediaFrame';
import ThumbnailPreviewFrame from './ThumbnailPreviewFrame';
import MediaTile from './MediaTile';
import type { ModuleUploadStatus } from './ModuleItemCard';

export interface IntroductionFormData {
  order: number;
  /** Original order from server — used to preserve media when introductions are reordered or removed. */
  sourceOrder?: number;
  title: string;
  transcript?: string;
  transcriptTimestamps?: number[];
  transcriptFileName?: string | null;
  file?: File | null;
  thumbnail?: File | null;
  thumbnailPreview?: string;
  fileUrl?: string;
  type?: 'video';
  mediaStatus?: string;
  mediaErrorMessage?: string;
  mediaUploaded?: boolean;
  fileUploadStatus?: ModuleUploadStatus;
  fileUploadProgress?: number;
  fileUploadError?: string;
  thumbnailUploadStatus?: ModuleUploadStatus;
  thumbnailUploadProgress?: number;
  thumbnailUploadError?: string;
  removingTarget?: 'file' | 'thumbnail' | null;
}

interface IntroductionItemCardProps {
  item: IntroductionFormData;
  index: number;
  contentId?: string;
  onUpdate: (field: keyof IntroductionFormData, value: unknown) => void;
  onUploadMedia?: (payload: { file?: File; thumbnail?: File }) => void;
  onRemoveMedia?: (target: 'file' | 'thumbnail') => void;
  onRemove: () => void;
  canRemove: boolean;
}

const mediaStatusLabel = (status?: string): { color: string; text: string } | null => {
  switch (status) {
    case 'pending':
      return { color: 'warning', text: 'Transcode queued' };
    case 'processing':
      return { color: 'info', text: 'Transcoding…' };
    case 'ready':
      return { color: 'success', text: 'Ready' };
    case 'failed':
      return { color: 'danger', text: 'Transcode failed' };
    default:
      return null;
  }
};

const IntroductionItemCard: React.FC<IntroductionItemCardProps> = ({
  item,
  index,
  contentId,
  onUpdate,
  onUploadMedia,
  onRemoveMedia,
  onRemove,
  canRemove,
}) => {
  const hasMedia = Boolean(item.file || item.fileUrl);
  const hasThumbnail = Boolean(item.thumbnail || item.thumbnailPreview);
  const transcodeBadge = mediaStatusLabel(item.mediaStatus);
  const hasTitle = Boolean(item.title?.trim());
  const isTargetUploading = (target: 'file' | 'thumbnail') =>
    target === 'file'
      ? item.fileUploadStatus === 'uploading'
      : item.thumbnailUploadStatus === 'uploading';
  const isTargetBusy = (target: 'file' | 'thumbnail') =>
    item.removingTarget === target || isTargetUploading(target);
  const fileUploadDisabled = !hasTitle || isTargetBusy('file');
  const thumbnailUploadDisabled = !hasTitle || isTargetBusy('thumbnail');
  const hasTranscript = Boolean(
    item.transcriptFileName ||
      item.transcript?.trim() ||
      (item.transcriptTimestamps && item.transcriptTimestamps.length > 0)
  );
  const [confirmRemove, setConfirmRemove] = useState<'file' | 'thumbnail' | null>(null);

  const clearTranscript = () => {
    onUpdate('transcriptFileName', null);
    onUpdate('transcript', '');
    onUpdate('transcriptTimestamps', []);
  };

  const triggerUpload = (payload: { file?: File; thumbnail?: File }) => {
    if (!hasTitle) {
      toast.info('Add a title for this introduction before uploading media.');
      return;
    }
    if (!contentId) {
      toast.info('Save content once to enable auto-upload for introductions.');
      return;
    }
    onUploadMedia?.(payload);
  };

  const isServerBacked = (target: 'file' | 'thumbnail') =>
    Boolean(contentId) &&
    (target === 'file'
      ? Boolean(item.fileUrl)
      : Boolean(item.thumbnailPreview && !item.thumbnail));

  const requestRemove = (target: 'file' | 'thumbnail') => {
    if (isTargetBusy(target)) return;
    if (isServerBacked(target)) {
      setConfirmRemove(target);
      return;
    }
    onRemoveMedia?.(target);
  };

  const confirmRemoveMedia = () => {
    if (confirmRemove) {
      onRemoveMedia?.(confirmRemove);
      setConfirmRemove(null);
    }
  };

  const onIntroductionVideoDrop = (acceptedFiles: File[]) => {
    if (!hasTitle || acceptedFiles.length === 0) return;
    const droppedFile = acceptedFiles[0];
    onUpdate('type', 'video');
    onUpdate('file', droppedFile);
    triggerUpload({ file: droppedFile });
  };

  const onIntroductionThumbnailDrop = (acceptedFiles: File[]) => {
    if (!hasTitle || acceptedFiles.length === 0) return;
    const thumbnailFile = acceptedFiles[0];
    onUpdate('thumbnail', thumbnailFile);
    onUpdate('thumbnailPreview', URL.createObjectURL(thumbnailFile));
    triggerUpload({ thumbnail: thumbnailFile });
  };

  const {
    getRootProps: getFileProps,
    getInputProps: getFileInputProps,
    isDragActive: isFileDragActive,
    isDragAccept: isFileDragAccept,
    isDragReject: isFileDragReject,
  } = useDropzone({
    accept: { 'video/mp4': ['.mp4'], 'video/quicktime': ['.mov'], 'video/x-msvideo': ['.avi'] },
    maxFiles: 1,
    disabled: fileUploadDisabled,
    onDrop: onIntroductionVideoDrop,
  });

  const {
    getRootProps: getThumbnailProps,
    getInputProps: getThumbnailInputProps,
    isDragActive: isThumbnailDragActive,
    isDragAccept: isThumbnailDragAccept,
    isDragReject: isThumbnailDragReject,
  } = useDropzone({
    accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/webp': ['.webp'] },
    maxFiles: 1,
    disabled: thumbnailUploadDisabled,
    onDrop: onIntroductionThumbnailDrop,
  });

  const handleSrtFile = (srtFile: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = (reader.result as string) || '';
        const { transcript, transcriptTimestamps } = parseSrt(text);
        onUpdate('transcript', transcript);
        onUpdate('transcriptTimestamps', transcriptTimestamps);
        onUpdate('transcriptFileName', srtFile.name);
      } catch {
        // Invalid SRT; leave transcript unchanged
      }
    };
    reader.readAsText(srtFile);
  };

  const { getRootProps: getTranscriptProps, getInputProps: getTranscriptInputProps } = useDropzone({
    accept: {
      'text/plain': ['.srt'],
      'application/x-subrip': ['.srt'],
      'text/x-srt': ['.srt'],
      'application/octet-stream': ['.srt'],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length > 0) {
        handleSrtFile(acceptedFiles[0]);
      } else if (rejectedFiles.length > 0 && rejectedFiles[0].file.name.toLowerCase().endsWith('.srt')) {
        handleSrtFile(rejectedFiles[0].file);
      }
    },
  });

  return (
    <Card className="mb-3 border">
      <CardBody className="pb-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <i className="ri-video-line me-2 text-primary"></i>
            Introduction {index + 1}
          </h6>
          {canRemove && (
            <Button color="danger" size="sm" outline type="button" onClick={onRemove}>
              <i className="ri-delete-bin-line"></i> Remove intro
            </Button>
          )}
        </div>
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label>
                Title <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Introduction title"
                value={item.title}
                onChange={(e) => onUpdate('title', e.target.value)}
                required
              />
            </FormGroup>
          </Col>
          {(item.fileUploadStatus === 'uploading' ||
            item.thumbnailUploadStatus === 'uploading' ||
            item.fileUploadStatus === 'error' ||
            item.thumbnailUploadStatus === 'error') && (
            <Col md={12}>
              {item.fileUploadStatus === 'uploading' && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Uploading video…</span>
                    <span className="text-muted">{item.fileUploadProgress ?? 0}%</span>
                  </div>
                  <Progress value={item.fileUploadProgress ?? 0} color="primary" />
                </div>
              )}
              {item.thumbnailUploadStatus === 'uploading' && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Uploading thumbnail…</span>
                    <span className="text-muted">{item.thumbnailUploadProgress ?? 0}%</span>
                  </div>
                  <Progress value={item.thumbnailUploadProgress ?? 0} color="info" />
                </div>
              )}
              {item.fileUploadStatus === 'error' && item.fileUploadError && (
                <Alert color="danger" className="py-2 small mb-3">
                  Video upload failed: {item.fileUploadError}. Drop the file again or save to retry.
                </Alert>
              )}
              {item.thumbnailUploadStatus === 'error' && item.thumbnailUploadError && (
                <Alert color="danger" className="py-2 small mb-3">
                  Thumbnail upload failed: {item.thumbnailUploadError}. Drop the image again or save to retry.
                </Alert>
              )}
            </Col>
          )}
          <Col md={12} className="mb-1">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="small fw-semibold text-muted">Intro media</span>
              {hasMedia && (
                <Badge color="primary" className="fw-normal">Video</Badge>
              )}
              {transcodeBadge && (
                <Badge color={transcodeBadge.color} className="fw-normal">
                  {transcodeBadge.text}
                </Badge>
              )}
              <span className="small text-muted">Vertical video · any thumbnail shape</span>
              {!hasTitle && (
                <span className="small text-warning">
                  <i className="ri-information-line me-1" />
                  Add a title above to enable uploads
                </span>
              )}
            </div>
          </Col>
          {item.mediaStatus === 'failed' && (
            <Col md={12}>
              <Alert color="warning" className="py-2 small mb-2">
                <strong>Transcode failed.</strong>{' '}
                {item.mediaErrorMessage ||
                  'The video uploaded, but converting it to streaming (HLS) failed. Drop the video again to retry, or check AWS MediaConvert on the server.'}
              </Alert>
            </Col>
          )}
          <Col md={12}>
            <Row className="justify-content-center g-4 mb-1">
              <Col xs="auto">
                <MediaTile
                  label="Video"
                  iconClass="ri-smartphone-line"
                  layout="portrait"
                  hasContent={hasMedia}
                  disabled={fileUploadDisabled}
                  removeDisabled={isTargetBusy('file')}
                  isRemoving={item.removingTarget === 'file'}
                  isUploading={item.fileUploadStatus === 'uploading'}
                  uploadProgress={item.fileUploadProgress ?? 0}
                  uploadLabel="Uploading video…"
                  emptyHint={hasTitle ? 'Drop vertical video' : 'Add a title first'}
                  replaceHint={hasTitle ? 'Drop or click to replace' : 'Add a title first'}
                  isDragActive={isFileDragActive}
                  isDragAccept={isFileDragAccept}
                  isDragReject={isFileDragReject}
                  dragAcceptLabel="Drop video"
                  dragRejectLabel="Use a video file only"
                  getRootProps={getFileProps}
                  getInputProps={getFileInputProps}
                  onRemoveClick={hasMedia ? () => requestRemove('file') : undefined}
                >
                  {item.file ? (
                    <PortraitMediaFrame poster={item.thumbnailPreview}>
                      <div className="text-center px-2">
                        <i className="ri-film-line fs-2 text-white d-block mb-2" />
                        <p className="mb-0 text-white small text-truncate">{item.file.name}</p>
                        {!contentId && (
                          <Badge color="warning" className="mt-2 fw-normal">Pending save</Badge>
                        )}
                      </div>
                    </PortraitMediaFrame>
                  ) : item.fileUrl ? (
                    <PortraitMediaFrame poster={item.thumbnailPreview}>
                      <HlsVideoPlayer
                        src={item.fileUrl}
                        poster={item.thumbnailPreview}
                        controls
                        fit="contain"
                        className="w-100 h-100"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </PortraitMediaFrame>
                  ) : null}
                </MediaTile>
              </Col>
              <Col xs="auto">
                <MediaTile
                  label="Thumbnail"
                  iconClass="ri-image-line"
                  layout="thumbnail"
                  hasContent={hasThumbnail}
                  disabled={thumbnailUploadDisabled}
                  removeDisabled={isTargetBusy('thumbnail')}
                  isRemoving={item.removingTarget === 'thumbnail'}
                  isUploading={item.thumbnailUploadStatus === 'uploading'}
                  uploadProgress={item.thumbnailUploadProgress ?? 0}
                  uploadLabel="Uploading thumbnail…"
                  emptyHint={hasTitle ? 'Drop cover image (any orientation)' : 'Add a title first'}
                  replaceHint={hasTitle ? 'Drop or click to replace' : 'Add a title first'}
                  isDragActive={isThumbnailDragActive}
                  isDragAccept={isThumbnailDragAccept}
                  isDragReject={isThumbnailDragReject}
                  dragAcceptLabel="Drop thumbnail"
                  dragRejectLabel="Use an image file"
                  getRootProps={getThumbnailProps}
                  getInputProps={getThumbnailInputProps}
                  onRemoveClick={hasThumbnail ? () => requestRemove('thumbnail') : undefined}
                >
                  {item.thumbnailPreview ? (
                    <ThumbnailPreviewFrame src={item.thumbnailPreview} alt="Introduction thumbnail" />
                  ) : item.thumbnail ? (
                    <ThumbnailPreviewFrame>
                      <div className="text-center px-2 text-muted">
                        <i className="ri-image-add-line fs-2 d-block mb-2" />
                        <p className="mb-0 small text-truncate">{item.thumbnail.name}</p>
                        {!contentId && (
                          <Badge color="warning" className="mt-2 fw-normal">Pending save</Badge>
                        )}
                      </div>
                    </ThumbnailPreviewFrame>
                  ) : null}
                </MediaTile>
              </Col>
            </Row>
          </Col>
          <Col md={12}>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="mb-0 d-block">Upload transcript (.srt only)</Label>
                {hasTranscript && (
                  <Button
                    type="button"
                    color="link"
                    size="sm"
                    className="p-0 text-danger"
                    onClick={clearTranscript}
                    title="Remove transcript"
                  >
                    <i className="ri-delete-bin-line me-1" />
                    Remove
                  </Button>
                )}
              </div>
              <div
                {...getTranscriptProps()}
                className="border border-2 border-dashed rounded p-2 text-center mb-2 small"
                style={{ cursor: 'pointer' }}
              >
                <input {...getTranscriptInputProps()} />
                {item.transcriptFileName ? (
                  <>
                    <i className="ri-file-text-line me-1 text-success"></i>
                    <span className="fw-semibold">{item.transcriptFileName}</span>
                    {item.transcriptTimestamps && item.transcriptTimestamps.length > 0 && (
                      <span className="text-muted d-block">({item.transcriptTimestamps.length} timestamps)</span>
                    )}
                  </>
                ) : (item.transcript?.trim() || (item.transcriptTimestamps && item.transcriptTimestamps.length > 0)) ? (
                  <>
                    <i className="ri-file-text-line me-1 text-success"></i>
                    <span className="fw-semibold">Transcript saved</span>
                    <span className="text-muted d-block">
                      {item.transcriptTimestamps?.length ?? 0} timestamp(s) — drop new .srt to replace
                    </span>
                  </>
                ) : (
                  <>
                    <i className="ri-upload-cloud-line me-1"></i>
                    Drop .srt or click to browse
                  </>
                )}
              </div>
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label>Transcript</Label>
              <Input
                type="textarea"
                rows={3}
                placeholder="Introduction transcript (or upload .srt above)"
                value={item.transcript || ''}
                onChange={(e) => onUpdate('transcript', e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      </CardBody>

      <Modal isOpen={confirmRemove !== null} toggle={() => setConfirmRemove(null)} centered size="sm">
        <ModalHeader toggle={() => setConfirmRemove(null)}>
          Remove {confirmRemove === 'file' ? 'video' : 'thumbnail'}?
        </ModalHeader>
        <ModalBody className="small">
          {confirmRemove === 'file' ? (
            <>
              This will permanently delete the video for <strong>Introduction {index + 1}</strong>.
              Upload again when you are ready.
            </>
          ) : (
            <>
              Remove the thumbnail for <strong>Introduction {index + 1}</strong>? You can upload a new one anytime.
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmRemove(null)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmRemoveMedia}>
            Remove
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default IntroductionItemCard;
